import { api, type PersonalInfo } from '@/portal-api.definition';
import { ActionType, ImageProfile, mage, useI18n, useToast } from '@nosinovacao/nosid-mfe-common';
import { queryOptions } from '@tanstack/react-query';
import { useNosIdMutation, useNosIdQuery } from './base.hooks';

function updatePersonalInfoPamPictureUri(personalInfo: PersonalInfo) {
  personalInfo.PamPictureUri = mage.getImage(personalInfo.PamPictureUri, ImageProfile.App);
  return personalInfo;
}

export const personalInfoQueryOptions = queryOptions({
  queryKey: ['personal-info'],
  queryFn: () => api.get('/api/v1/personal-info').then(updatePersonalInfoPamPictureUri),
});

export function useGetPersonalInfo() {
  return useNosIdQuery(personalInfoQueryOptions);
}

export function useUpdatePersonalInfo() {
  const toast = useToast();
  const { t } = useI18n();
  return useNosIdMutation({
    mutationFn: (personalInfo: PersonalInfo) =>
      api.put('/api/v1/personal-info', personalInfo).then(updatePersonalInfoPamPictureUri),
    successLogAction: () => ({ ActionType: ActionType.Submit, Option: 'PersonalInfo' }),
    errorLogAction: () => ({ ActionType: ActionType.Submit, Option: 'PersonalInfo' }),
    onSuccess: (newPersonalInfo, _input, _result, { client }) => {
      client.setQueryData(personalInfoQueryOptions.queryKey, newPersonalInfo);
      toast.show('success', t('Common.SnackBar.PersonalInfoUpdated'));
    },
  });
}

export function useUpdateProfilePicture() {
  const toast = useToast();
  const { t } = useI18n();
  return useNosIdMutation({
    mutationFn: async (photo: Blob) => {
      const fd = new FormData();
      fd.set('image', photo);
      return await api.post('/api/v1/personal-info/profile-picture', fd);
    },
    onSuccess: (res, _input, _result, { client }) => {
      client.setQueryData(personalInfoQueryOptions.queryKey, (old) =>
        old ? updatePersonalInfoPamPictureUri({ ...old, PamPictureUri: res.Url }) : old,
      );
      toast.show('success', t('Common.SnackBar.ProfilePictureUpdated'));
    },
  });
}
